import { trackPhotoAccepted, trackPhotoRejected } from "../../diagnostics";

// Diagnostics for the browser-side passport photo checks (#244). Log only:
// nothing here changes a check, a message or a screen. Log-safe fields only
// (reason names, the Scanbot status, counts, line lengths, HTTP status, the page layout
// and MRZ strip ratio (#245), the original photo pixel size (#245), which OCR read gave
// the result (#245)); never MRZ text, names, document numbers, dates or images.

const DOC_TYPE = "Passport";

// Which MRZ strip ran (#245): fixed names and numbers from mrzCrop.js, never text.
const PAGE_LAYOUTS = ["two_page", "one_page", "unknown"];

function addMrzCrop(detail, mrzCrop) {
  if (!mrzCrop) return detail;
  if (PAGE_LAYOUTS.includes(mrzCrop.pageLayout)) detail.pageLayout = mrzCrop.pageLayout;
  if (Number.isFinite(mrzCrop.mrzCropRatio)) detail.mrzCropRatio = mrzCrop.mrzCropRatio;
  if (Number.isFinite(mrzCrop.docAspect)) detail.docAspect = mrzCrop.docAspect;
  return detail;
}

// Pixel size of the original photo (#245). Numbers only; left out if unreadable.
function addPhotoSize(detail, photoSize) {
  if (!photoSize) return detail;
  if (Number.isFinite(photoSize.width)) detail.pixelWidth = photoSize.width;
  if (Number.isFinite(photoSize.height)) detail.pixelHeight = photoSize.height;
  return detail;
}

// Which OCR read gave the result (#245): numbers, fixed words and an error class name only.
const UPSCALE_SKIPS = ["unsupported", "no_canvas", "too_large", "error", "ocr_error"];

function addMrzRead(detail, mrzRead) {
  if (!mrzRead) return detail;
  if (Number.isFinite(mrzRead.mrzOcrScale)) detail.mrzOcrScale = mrzRead.mrzOcrScale;
  if (mrzRead.mrzOcrFallback === true) detail.mrzOcrFallback = true;
  const lengths = mrzRead.upscaledLineLengths;
  if (Array.isArray(lengths) && lengths.every(Number.isFinite)) detail.upscaledLineLengths = lengths.slice(0, 10);
  if (UPSCALE_SKIPS.includes(mrzRead.mrzUpscale)) detail.mrzUpscale = mrzRead.mrzUpscale;
  if (typeof mrzRead.mrzUpscaleError === "string") detail.mrzUpscaleError = mrzRead.mrzUpscaleError.slice(0, 40);
  return detail;
}

// The only fields copied from a DocumentProcessingError's `diagnostic`, so
// document text can never ride along by mistake.
const DIAGNOSTIC_FIELDS = ["detectionStatus", "detectSuccess", "ocrLineCount", "ocrLineLengths"];

// Photo tries for one verification, kept in sessionStorage per validation ID so
// the count survives a page reload in the same tab (for example when iOS
// reloads the page after the camera app). Falls back to memory when storage is
// blocked.
const ATTEMPT_KEY_PREFIX = "ekycPassportPhotoAttempt:";
const memoryAttempts = {};

const attemptKey = (validationId) => ATTEMPT_KEY_PREFIX + (validationId || "unknown");

function currentAttempt(validationId) {
  const key = attemptKey(validationId);
  let stored = 0;
  try {
    stored = Number(window.sessionStorage.getItem(key)) || 0;
  } catch (e) {
    /* storage blocked: memory only */
  }
  return Math.max(stored, memoryAttempts[key] || 0);
}

function nextAttempt(validationId) {
  const key = attemptKey(validationId);
  const next = currentAttempt(validationId) + 1;
  memoryAttempts[key] = next;
  try {
    window.sessionStorage.setItem(key, String(next));
  } catch (e) {
    /* storage blocked: memory only */
  }
  return next;
}

export function logPassportPhotoAccepted(validationId, mrzCrop = null, photoSize = null, mrzRead = null) {
  try {
    const detail = { docType: DOC_TYPE, attempt: nextAttempt(validationId) };
    addMrzCrop(detail, mrzCrop);
    addPhotoSize(detail, photoSize);
    addMrzRead(detail, mrzRead);
    trackPhotoAccepted(detail);
  } catch (e) {
    /* diagnostics must never break the eKYC flow */
  }
}

export function logPassportPhotoRejected(error, validationId, mrzCrop = null, photoSize = null, mrzRead = null) {
  try {
    const diagnostic = (error && error.diagnostic) || {};
    const detail = { docType: DOC_TYPE, reason: diagnostic.reason || "unknown", attempt: nextAttempt(validationId) };
    for (const field of DIAGNOSTIC_FIELDS) {
      if (diagnostic[field] !== undefined) detail[field] = diagnostic[field];
    }
    if (!diagnostic.reason) {
      detail.errorName = error && typeof error.name === "string" ? error.name : null;
    }
    addMrzCrop(detail, mrzCrop);
    addPhotoSize(detail, photoSize);
    addMrzRead(detail, mrzRead);
    trackPhotoRejected(detail);
  } catch (e) {
    /* diagnostics must never break the eKYC flow */
  }
}

// The upload of a photo that already passed the checks. Not a new try, so it
// keeps that try's number.
export function logPassportUploadFailed(httpStatus, validationId) {
  try {
    trackPhotoRejected({
      docType: DOC_TYPE,
      reason: "upload_failed",
      uploadStatus: typeof httpStatus === "number" ? httpStatus : "network_error",
      attempt: currentAttempt(validationId),
    });
  } catch (e) {
    /* diagnostics must never break the eKYC flow */
  }
}

// For tests only: start every count again.
export function resetPassportScanAttempts() {
  for (const key of Object.keys(memoryAttempts)) delete memoryAttempts[key];
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(ATTEMPT_KEY_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch (e) {
    /* storage blocked */
  }
}
